import type { QueryClient } from '@tanstack/react-query';
import type { TaxonomyChangedPart } from '@inference.engine/taxonomy/web';
import {
  TAXONOMY_DATASET_QUERY_KEYS,
  type TaxonomyDataset,
  type TaxonomyRecord,
} from '@/entities/taxonomy-dataset';
import { TAXONOMY_QUERY_KEYS } from '@/entities/taxonomy';
import { TAXONOMY_LIST_QUERY_KEYS } from '@/entities/taxonomy-list';

/**
 * Writes what the agent saved into the page's caches, so the grid updates in place: no refetch,
 * no skeleton, and the grid's selection and open editor stay where they are.
 *
 * A row the person has unsaved edits on is left exactly as cached, revision included, so their
 * next Save comes back conflicted rather than overwriting the agent's write unseen. A row the
 * agent removed is dropped, and any unsaved edits on it with it.
 *
 * The dataset detail is set, never invalidated: the records query sits under its key, and a
 * refetch would hand dirty rows the new revision this function withholds. A records fetch already
 * in flight would land over the patch, so the change is applied again once it settles.
 */
export function applyTaxonomyChange(
  queryClient: QueryClient,
  part: TaxonomyChangedPart,
  dirtyRecordIds: ReadonlySet<string>,
): { changedIds: string[] } {
  const result = applyOnce(queryClient, part, dirtyRecordIds);

  const cache = queryClient.getQueryCache();
  const recordsQuery = cache.find({
    queryKey: TAXONOMY_DATASET_QUERY_KEYS.records(part.datasetId),
    exact: true,
  });
  if (recordsQuery?.state.fetchStatus === 'fetching') {
    const unsubscribe = cache.subscribe((event) => {
      if (event.query !== recordsQuery || recordsQuery.state.fetchStatus === 'fetching') return;
      unsubscribe();
      applyOnce(queryClient, part, dirtyRecordIds);
    });
  }
  return result;
}

function applyOnce(
  queryClient: QueryClient,
  part: TaxonomyChangedPart,
  dirtyRecordIds: ReadonlySet<string>,
): { changedIds: string[] } {
  const detailKey = TAXONOMY_DATASET_QUERY_KEYS.detail(part.datasetId);
  const dataset = queryClient.getQueryData<TaxonomyDataset>(detailKey);
  if (!dataset) {
    return { changedIds: [] };
  }

  const changedIds: string[] = [];
  const recordsKey = TAXONOMY_DATASET_QUERY_KEYS.records(part.datasetId);
  const versionId = part.dataset?.taxonomyVersionId ?? dataset.taxonomyVersionId;
  const now = new Date().toISOString();
  const cached = queryClient.getQueryData<TaxonomyRecord[]>(recordsKey);

  if (cached) {
    const removed = new Set(part.removedIds ?? []);
    const incoming = new Map((part.records ?? []).map((r) => [r.id, r]));
    const next = cached
      .filter((record) => !removed.has(record.id))
      .map((record): TaxonomyRecord => {
        const change = incoming.get(record.id);
        incoming.delete(record.id);
        const repinned = { ...record, taxonomyVersionId: versionId };
        // A newer revision in the cache is a later write, which this one must not undo.
        if (!change || dirtyRecordIds.has(record.id) || record.revision > change.revision) {
          return part.dataset?.taxonomyVersionId ? repinned : record;
        }
        if (record.revision < change.revision) changedIds.push(record.id);
        return {
          ...repinned,
          cells: change.cells,
          assembledName: change.assembledName,
          outputs: change.outputs,
          revision: change.revision,
          sortKey: change.sortKey ?? record.sortKey,
          updatedAt: now,
        };
      });
    for (const change of incoming.values()) {
      changedIds.push(change.id);
      next.push({
        id: change.id,
        datasetId: part.datasetId,
        taxonomyVersionId: versionId,
        cells: change.cells,
        assembledName: change.assembledName,
        outputs: change.outputs,
        revision: change.revision,
        sortKey: change.sortKey ?? Number.MAX_SAFE_INTEGER,
        createdAt: now,
        updatedAt: now,
      });
    }
    next.sort((a, b) => a.sortKey - b.sortKey);
    queryClient.setQueryData(recordsKey, next);
    queryClient.setQueryData<TaxonomyDataset>(detailKey, {
      ...dataset,
      ...part.dataset,
      recordCount: next.length,
    });
  } else if (part.dataset) {
    queryClient.setQueryData<TaxonomyDataset>(detailKey, { ...dataset, ...part.dataset });
  }

  if (part.dataset || part.records?.length || part.removedIds?.length) {
    // The list shows names, campaigns, counts and when each was last edited, and sorts by it.
    void queryClient.invalidateQueries({
      queryKey: TAXONOMY_DATASET_QUERY_KEYS.list(),
      exact: true,
    });
  }
  if (part.schemaChanged) {
    void queryClient.invalidateQueries({
      queryKey: TAXONOMY_QUERY_KEYS.detail(dataset.taxonomyId),
    });
    void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.list(), exact: true });
    // The new version may pin a list version published after these lists were fetched.
    void queryClient.invalidateQueries({ queryKey: TAXONOMY_LIST_QUERY_KEYS.all });
  }
  return { changedIds };
}
